import React, { useState } from 'react';
import {
  Phone, Mail, MapPin, BellRing, MessageSquare, Edit, FileText,
  Building2, XCircle, Trophy, ArrowLeft, Target, ChevronRight,
} from 'lucide-react';

/**
 * Read-only Kanban card for a lead that has already been converted into a
 * project (status === 'won'). Only TWO actions: jump to the project, or
 * convert it back to a lead.
 */
function ConvertedKanbanCard({ lead, getServiceBadge, fmtCurrency, navigate, openRevertModal, handleConvertToProject }) {
  const svc = getServiceBadge(lead.service_type);
  const hasProject = !!lead.project_id;
  return (
    <div
      className="bg-emerald-50/60 border-2 border-emerald-200 rounded-lg p-3 shadow-sm"
      data-testid={`converted-kanban-${lead.id}`}
    >
      <div className="flex items-start justify-between mb-2">
        <h4 className="font-bold text-sm text-emerald-900 truncate flex-1 flex items-center gap-1.5">
          <Trophy className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
          {lead.customer_name}
        </h4>
        <span
          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold text-white ml-2 flex-shrink-0"
          style={{ backgroundColor: svc.color }}
        >
          {svc.label}
        </span>
      </div>

      <div className="space-y-0.5 text-[11px] text-gray-600 mb-2">
        {lead.customer_phone && (
          <div className="flex items-center gap-1"><Phone className="w-3 h-3" />{lead.customer_phone}</div>
        )}
        {lead.customer_email && (
          <div className="flex items-center gap-1 truncate"><Mail className="w-3 h-3 shrink-0" /><span className="truncate">{lead.customer_email}</span></div>
        )}
        {lead.customer_address && (
          <div className="flex items-center gap-1 truncate"><MapPin className="w-3 h-3 shrink-0" /><span className="truncate">{lead.customer_address}</span></div>
        )}
      </div>

      {hasProject ? (
        <div className="bg-white/70 border border-emerald-100 rounded p-2 mb-2 space-y-0.5 text-[11px] text-emerald-900">
          {lead.project_reference && (<div><strong>Project:</strong> {lead.project_reference}</div>)}
          {fmtCurrency(lead.project_value) && (<div><strong>Value:</strong> {fmtCurrency(lead.project_value)}</div>)}
          {lead.project_install_date && (<div><strong>Install:</strong> {lead.project_install_date}</div>)}
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 rounded p-2 mb-2 text-[11px] text-amber-900" data-testid={`won-no-project-${lead.id}`}>
          Marked as Won but no project yet. Create one to start tracking install & invoices.
        </div>
      )}

      <div className="grid grid-cols-1 gap-1.5">
        {hasProject ? (
          <button
            data-testid={`go-to-project-${lead.id}`}
            onClick={(e) => {
              e.stopPropagation();
              if (lead.project_id) navigate(`/projects/${lead.project_id}`);
            }}
            className="text-xs py-2 px-2 rounded bg-emerald-600 hover:bg-emerald-700 text-white flex items-center justify-center gap-1.5 font-bold"
          >
            <Building2 className="w-3.5 h-3.5" /> Go to project
          </button>
        ) : (
          <button
            data-testid={`create-project-${lead.id}`}
            onClick={(e) => { e.stopPropagation(); handleConvertToProject && handleConvertToProject(lead); }}
            className="text-xs py-2 px-2 rounded bg-emerald-600 hover:bg-emerald-700 text-white flex items-center justify-center gap-1.5 font-bold"
          >
            <Building2 className="w-3.5 h-3.5" /> Create project
          </button>
        )}
        <button
          data-testid={`revert-to-lead-${lead.id}`}
          onClick={(e) => { e.stopPropagation(); openRevertModal(lead); }}
          className="text-xs py-2 px-2 rounded bg-white border border-emerald-300 hover:bg-emerald-50 text-emerald-800 flex items-center justify-center gap-1.5 font-semibold"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Convert back to lead
        </button>
      </div>
    </div>
  );
}

/**
 * Single lead card used in the Kanban board. Renders the
 * ConvertedKanbanCard variant for status='won' leads. All click handlers come
 * from the parent (ManageLeads) so the card stays a pure presentation piece.
 *
 * Status changes are done via the inline dropdown on the card itself —
 * drag-and-drop was removed (it was finicky on long boards/laptops) in
 * favour of a single click + select.
 */
export default function KanbanCard({
  lead,
  suggestion,
  getServiceBadge,
  fmtCurrency,
  navigate,
  openRevertModal,
  openDetailModal,
  openCallLogModal,
  openEditModal,
  openQuoteChooser,
  handleConvertToProject,
  handleArchiveLead,
  handleStatusChange,
  statusOptions,
}) {
  if (lead.status === 'won') {
    return (
      <ConvertedKanbanCard
        lead={lead}
        getServiceBadge={getServiceBadge}
        fmtCurrency={fmtCurrency}
        navigate={navigate}
        openRevertModal={openRevertModal}
        handleConvertToProject={handleConvertToProject}
      />
    );
  }

  const svc = getServiceBadge(lead.service_type);

  return (
    <div
      className="bg-white rounded-lg p-3 shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
      data-testid={`kanban-lead-${lead.id}`}
    >
      <div className="flex items-start justify-between mb-2">
        <h4
          onClick={(e) => { e.stopPropagation(); openDetailModal(lead); }}
          className="font-semibold text-sm text-gray-900 truncate flex-1 cursor-pointer hover:text-[#006400] hover:underline"
        >
          {lead.customer_name}
        </h4>
        <span
          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold text-white ml-2 flex-shrink-0"
          style={{ backgroundColor: svc.color }}
        >
          {svc.label}
        </span>
      </div>

      {lead.customer_phone && (
        <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
          <Phone className="w-3 h-3" />
          <span>{lead.customer_phone}</span>
        </div>
      )}

      <SuggestedInstallersChip suggestion={suggestion} leadId={lead.id} />

      {lead.project_size && (
        <div className="text-xs text-gray-500 mb-2">Size: {lead.project_size}</div>
      )}

      {lead.latest_quote_m2 != null && (
        <div
          className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#006400] bg-[#006400]/10 border border-[#006400]/20 rounded-full px-2 py-0.5 mb-2"
          title={`Quote ${lead.latest_quote_reference || ''} · status: ${lead.latest_quote_status || '—'}`}
          data-testid={`kanban-quote-pill-${lead.id}`}
        >
          📐 {lead.latest_quote_m2}m²
          {lead.latest_quote_status === 'sent' && <span className="text-[9px] uppercase tracking-wider opacity-80">· sent</span>}
          {lead.latest_quote_status === 'draft' && <span className="text-[9px] uppercase tracking-wider opacity-80">· draft</span>}
        </div>
      )}

      {lead.reminder_date && (
        <div className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 rounded px-2 py-1 mb-2">
          <BellRing className="w-3 h-3" />
          <span>Reminder: {lead.reminder_date}</span>
        </div>
      )}

      {lead.call_logs && lead.call_logs.length > 0 && (
        <div className="flex items-center gap-1 text-xs text-gray-400 mb-2">
          <MessageSquare className="w-3 h-3" />
          <span>{lead.call_logs.length} note{lead.call_logs.length !== 1 ? 's' : ''}</span>
        </div>
      )}

      {/* Inline status mover — replaces drag-and-drop. One click → pick → done.
          Picking "Won" without an existing project intercepts to the
          Convert-to-Project flow so the lead never lands in Won orphaned. */}
      {handleStatusChange && statusOptions && (
        <div className="mb-2">
          <label className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold block mb-0.5">Move to</label>
          <select
            value={lead.status || ''}
            onChange={(e) => {
              e.stopPropagation();
              const next = e.target.value;
              if (!next || next === lead.status) return;
              if (next === 'won' && !lead.project_id && handleConvertToProject) {
                handleConvertToProject(lead);
              } else {
                handleStatusChange(lead.id, next);
              }
            }}
            onClick={(e) => e.stopPropagation()}
            data-testid={`kanban-status-${lead.id}`}
            className="w-full text-xs px-2 py-1.5 border border-gray-200 rounded bg-white focus:outline-none focus:ring-2 focus:ring-[#006400]/30 cursor-pointer"
            aria-label={`Change status for ${lead.customer_name}`}
          >
            {statusOptions.map(([value, cfg]) => (
              <option key={value} value={value}>{cfg.label}</option>
            ))}
          </select>
        </div>
      )}

      <div className="grid grid-cols-2 gap-1.5 pt-2 border-t border-gray-100">
        <button
          onClick={(e) => { e.stopPropagation(); openCallLogModal(lead); }}
          className="text-xs py-2 px-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-600 flex flex-col items-center justify-center gap-0.5"
          title="Notes"
        >
          <MessageSquare className="w-3.5 h-3.5" />
          <span className="text-[10px] leading-tight">Notes</span>
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); openEditModal(lead); }}
          className="text-xs py-2 px-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-600 flex flex-col items-center justify-center gap-0.5"
          title="Edit Lead"
        >
          <Edit className="w-3.5 h-3.5" />
          <span className="text-[10px] leading-tight">Edit</span>
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); openQuoteChooser(lead); }}
          className="text-xs py-2 px-1 rounded bg-[#006400] hover:bg-[#004d00] text-white flex flex-col items-center justify-center gap-0.5"
          title="Quote Customer"
          data-testid={`kanban-quote-${lead.id}`}
        >
          <FileText className="w-3.5 h-3.5" />
          <span className="text-[10px] leading-tight">{lead.latest_quote_id ? 'Edit / New Quote' : 'Quote Customer'}</span>
        </button>
        {(lead.status === 'quoted' || lead.status === 'won') ? (
          <button
            onClick={(e) => { e.stopPropagation(); handleConvertToProject(lead); }}
            className="text-xs py-2 px-1 rounded bg-green-600 hover:bg-green-700 text-white flex flex-col items-center justify-center gap-0.5"
            title="Convert to Project"
            data-testid={`kanban-convert-${lead.id}`}
          >
            <Building2 className="w-3.5 h-3.5" />
            <span className="text-[10px] leading-tight">Convert to Project</span>
          </button>
        ) : (
          <button
            onClick={(e) => { e.stopPropagation(); handleArchiveLead(lead.id); }}
            className="text-xs py-2 px-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-600 flex flex-col items-center justify-center gap-0.5"
            title="Archive Lead"
            data-testid={`archive-lead-${lead.id}`}
          >
            <XCircle className="w-3.5 h-3.5" />
            <span className="text-[10px] leading-tight">Archive</span>
          </button>
        )}
      </div>
    </div>
  );
}


/**
 * Compact "Suggested installers" chip powered by
 * `GET /api/leads/suggestions`. Silent when we don't have a matched
 * territory yet (no postcode extracted / postcode unmapped) so the
 * card doesn't grow a placeholder row.
 *
 * Click to expand → shows the ranked list with "Exact" vs "Region"
 * badges so staff can pick the right installer at a glance.
 */
function SuggestedInstallersChip({ suggestion, leadId }) {
  const [open, setOpen] = useState(false);
  if (!suggestion || !suggestion.territory) return null;
  const matches = suggestion.matches || [];
  const exactCount = matches.filter(m => m.strength === 'exact').length;

  return (
    <div className="mb-2" data-testid={`suggested-installers-${leadId}`}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
        className={`w-full flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-2 py-1 border transition-colors ${
          matches.length === 0
            ? 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100'
            : exactCount > 0
              ? 'bg-emerald-50 text-emerald-900 border-emerald-200 hover:bg-emerald-100'
              : 'bg-amber-50 text-amber-900 border-amber-200 hover:bg-amber-100'
        }`}
        data-testid={`suggested-installers-toggle-${leadId}`}
        title={`${suggestion.postcode} · ${suggestion.territory}`}
      >
        <Target className="w-3 h-3 shrink-0" />
        <span className="truncate flex-1 text-left">
          {matches.length === 0
            ? `No installer covers ${suggestion.territory}`
            : `${matches.length} suggested · ${suggestion.territory}`}
        </span>
        <ChevronRight className={`w-3 h-3 shrink-0 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <div className="mt-1.5 rounded-lg border border-gray-200 bg-white p-2 space-y-1">
          {matches.length === 0 ? (
            <div className="text-[11px] text-gray-500 px-1">
              Nobody's ticked <strong>{suggestion.territory}</strong> or nearby yet. Chase an approved installer to add coverage.
            </div>
          ) : (
            matches.map((m) => (
              <div
                key={m.user_id}
                className="flex items-center justify-between gap-2"
                data-testid={`suggested-installer-${leadId}-${m.user_id}`}
              >
                <span className="text-[12px] font-medium text-gray-900 truncate">{m.name}</span>
                <span
                  className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full shrink-0 ${
                    m.strength === 'exact'
                      ? 'bg-emerald-600 text-white'
                      : 'bg-amber-100 text-amber-800 border border-amber-300'
                  }`}
                >
                  {m.strength === 'exact' ? 'Exact' : 'Region'}
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
